// Destino del correo de activación de cuenta (2026-09-13, cuentas solo por el
// admin). El enlace llega a `<subdominio>.logiacare.com/hce/paciente/activar/
// <token>`: la persona ve su usuario y su clínica, elige su contraseña y, al
// guardar, va directo al login de ESA clínica (mismo subdominio). Sin sesión
// de Medplum (ruta en PREFIJOS_SIN_MEDPLUM de main.tsx): habla con la bóveda
// (`/api/hce/activacion/:token`), que decide si fijar la contraseña en
// hce_users (admin) o en Medplum (Personal Médico / Director Técnico).
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { activarCuentaHce, consultarActivacionHce, type ActivacionHce } from '@/lib/hceVault';
import { ListaReglasContrasena } from './ListaReglasContrasena';
import { contrasenaCumplePolitica } from './reglasContrasena';

type EstadoEnlace = { fase: 'cargando' } | { fase: 'invalido'; error: string } | { fase: 'listo'; datos: ActivacionHce };

export function ActivarCuentaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const [enlace, setEnlace] = useState<EstadoEnlace>({ fase: 'cargando' });
  const [password, setPassword] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    void consultarActivacionHce(token).then((r) => {
      if (cancelado) return;
      setEnlace(r.ok ? { fase: 'listo', datos: r.datos } : { fase: 'invalido', error: r.error });
    });
    return () => {
      cancelado = true;
    };
  }, [token]);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirmacion) {
      setError(t('auth.activar.noCoinciden'));
      return;
    }
    setEnviando(true);
    const r = await activarCuentaHce(token, password);
    setEnviando(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    void navigate('/login?activada=1', { replace: true });
  }

  return (
    <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-hce-surface border border-border rounded-lg shadow-sm p-6 space-y-4">
        <h1 className="text-base font-medium text-hce-ink">{t('auth.activar.titulo')}</h1>
        {enlace.fase === 'cargando' && <p className="text-sm text-hce-muted">{t('auth.activar.cargando')}</p>}
        {enlace.fase === 'invalido' && (
          <Alert variant="red">
            <AlertDescription>{enlace.error}</AlertDescription>
          </Alert>
        )}
        {enlace.fase === 'listo' && (
          <form onSubmit={(e) => void enviar(e)} className="space-y-3">
            <div className="rounded-md border border-border p-3 text-sm space-y-1">
              <div>
                <span className="text-xs font-mono uppercase tracking-wide text-hce-muted mr-2">{t('auth.activar.clinica')}</span>
                <span className="text-hce-ink">{enlace.datos.clinicName}</span>
              </div>
              <div>
                <span className="text-xs font-mono uppercase tracking-wide text-hce-muted mr-2">{t('auth.activar.usuario')}</span>
                <span className="font-mono text-hce-ink">{enlace.datos.username}</span>
              </div>
            </div>
            {error && (
              <Alert variant="red">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div>
              <label htmlFor="activar-contrasena" className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">
                {t('auth.activar.contrasena')}
              </label>
              <Input id="activar-contrasena" type="password" minLength={12} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <div className="mt-1.5">
                <ListaReglasContrasena password={password} />
              </div>
            </div>
            <div>
              <label htmlFor="activar-confirmacion" className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">
                {t('auth.activar.confirmacion')}
              </label>
              <Input
                id="activar-confirmacion"
                type="password"
                minLength={12}
                autoComplete="new-password"
                value={confirmacion}
                onChange={(e) => setConfirmacion(e.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={enviando || !contrasenaCumplePolitica(password)} className="w-full">
              {enviando ? t('auth.activar.guardando') : t('auth.activar.guardar')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
